import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { getInvitationRetrieveQueryOptions, invitationActivate } from '@/api/client'
import { AuthShell } from '@/components/auth-shell'
import { PasswordChecklist } from '@/components/password-checklist'
import { passwordRequirements } from '@/lib/password-policy'
import { ForcedPasswordChange } from '@/components/forced-password-change'
import { TotpEnroll } from '@/components/totp-enroll'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi, hasPendingFlow, type AuthResponse } from '@/lib/auth-api'
import { sessionFromResponse, sessionQuery } from '@/lib/auth'

export const Route = createFileRoute('/account/activate/$token')({
  component: ActivatePage,
})

const NICK_MESSAGE =
  'Nick może zawierać litery (łacińskie i polskie), cyfry oraz znak podkreślenia. ' +
  'Długość 3–20 znaków.'

const NICK_REGEX = /^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ0-9_]{3,20}$/

/**
 * Feature 015: a teacher invitation activates a PRE-CREATED account — the
 * teacher has a real name, so there is no nickname (the field is hidden and
 * omitted from the payload). Admin/superadmin invitations keep nick+password.
 * One output shape (optional nickname) so the form typing stays uniform; the
 * staff-only nickname rule lives in the superRefine.
 */
export function activateSchemaFor(isTeacher: boolean) {
  return z
    .object({
      nickname: z.string().optional(),
      password: z
        .string()
        .refine(
          (value) => passwordRequirements.every((r) => r.test(value)),
          'Hasło nie spełnia wymagań',
        ),
      confirm: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!isTeacher && !NICK_REGEX.test(data.nickname ?? '')) {
        ctx.addIssue({ code: 'custom', message: NICK_MESSAGE, path: ['nickname'] })
      }
      if (data.password !== data.confirm) {
        ctx.addIssue({ code: 'custom', message: 'Hasła muszą być identyczne', path: ['confirm'] })
      }
    })
}

export function activationErrorMessage(status: number, data: unknown): string {
  if (status === 404) {
    return 'Link jest nieprawidłowy lub wygasł. Skontaktuj się z administratorem systemu.'
  }
  const body = (data ?? {}) as Record<string, unknown>
  for (const field of ['nickname', 'password', 'detail']) {
    const value = body[field]
    if (Array.isArray(value) && value.length) return String(value[0])
    if (typeof value === 'string') return value
  }
  return 'Nie udało się aktywować konta. Spróbuj ponownie.'
}

function ActivatePage() {
  const { token } = Route.useParams()
  const router = useRouter()
  const queryClient = useQueryClient()
  const [enrolling, setEnrolling] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)

  const invitation = useQuery(
    getInvitationRetrieveQueryOptions(token, {
      query: { retry: false, staleTime: Infinity },
    }),
  )
  const email = invitation.data?.status === 200 ? invitation.data.data.email : undefined
  const isTeacher = invitation.data?.status === 200 && invitation.data.data.role === 'teacher'

  const finishLogin = (response: AuthResponse) => {
    queryClient.setQueryData(sessionQuery.queryKey, sessionFromResponse(response))
    router.history.push('/')
  }

  return (
    <AuthShell>
      <Card>
        {invitation.isPending ? (
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Sprawdzanie zaproszenia…
          </CardContent>
        ) : !email ? (
          <CardContent className="py-10 text-center text-sm">
            {/* TR-100 §6: jeden komunikat dla wszystkich nieprawidłowych stanów. */}
            <p>Link jest nieprawidłowy lub wygasł. Skontaktuj się z administratorem systemu.</p>
          </CardContent>
        ) : changingPassword ? (
          <ForcedPasswordChange
            onSuccess={finishLogin}
            onLogout={() => router.history.push('/login?expired=ok')}
          />
        ) : enrolling ? (
          <TotpEnroll
            onSuccess={finishLogin}
            onPasswordChangeRequired={() => setChangingPassword(true)}
            onSwitchAccount={async () => {
              await authApi.logout()
              router.history.push('/login')
            }}
          />
        ) : (
          <ActivateForm
            token={token}
            email={email}
            isTeacher={isTeacher}
            onAuthenticated={finishLogin}
            onEnroll={() => setEnrolling(true)}
            onLoginFallback={() => router.history.push('/login?activated=ok')}
          />
        )}
      </Card>
    </AuthShell>
  )
}

function ActivateForm({
  token,
  email,
  isTeacher,
  onAuthenticated,
  onEnroll,
  onLoginFallback,
}: {
  token: string
  email: string
  isTeacher: boolean
  onAuthenticated: (response: AuthResponse) => void
  onEnroll: () => void
  onLoginFallback: () => void
}) {
  const [activateError, setActivateError] = useState<string | null>(null)

  const form = useForm({
    resolver: zodResolver(activateSchemaFor(isTeacher)),
    defaultValues: { nickname: '', password: '', confirm: '' },
  })
  const password = useWatch({ control: form.control, name: 'password' })

  const submit = form.handleSubmit(async ({ nickname, password }) => {
    setActivateError(null)
    const response = await invitationActivate(
      token,
      isTeacher ? { password } : { nickname, password },
    )
    if (response.status !== 201) {
      setActivateError(activationErrorMessage(response.status, response.data))
      return
    }
    // TR-100 krok 10 / TR-700: konto aktywne → ciche logowanie; kadra
    // administracyjna wpada w wymuszony enrollment 2FA, nauczyciel loguje się
    // od razu.
    const login = await authApi.login({ email, password })
    if (login.meta?.is_authenticated) {
      onAuthenticated(login)
    } else if (hasPendingFlow(login, 'mfa_enroll')) {
      onEnroll()
    } else {
      // Konto powstało, ale ciche logowanie nie doszło do skutku — na ekranie
      // logowania musi być jasne, że aktywacja SIĘ UDAŁA.
      onLoginFallback()
    }
  })

  return (
    <>
      <CardHeader>
        <CardTitle>Aktywacja konta</CardTitle>
        <CardDescription>
          {isTeacher
            ? 'Ustaw hasło, aby zalogować się do konta nauczyciela.'
            : 'Ustaw nick i hasło, a następnie skonfiguruj weryfikację dwuetapową.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">E-mail (login)</Label>
            <Input id="email" value={email} disabled />
          </div>
          {!isTeacher && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nickname">Nick</Label>
              <Input id="nickname" autoFocus {...form.register('nickname')} />
              {form.formState.errors.nickname && (
                <p className="text-sm text-destructive">{form.formState.errors.nickname.message}</p>
              )}
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">Hasło</Label>
            <Input
              id="password"
              type="password"
              autoFocus={isTeacher}
              autoComplete="new-password"
              {...form.register('password')}
            />
            <PasswordChecklist password={password} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm">Powtórz hasło</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              {...form.register('confirm')}
            />
            {form.formState.errors.confirm && (
              <p className="text-sm text-destructive">{form.formState.errors.confirm.message}</p>
            )}
          </div>
          {activateError && <p className="text-sm text-destructive">{activateError}</p>}
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {isTeacher ? 'Ustaw hasło' : 'Utwórz konto'}
          </Button>
        </form>
      </CardContent>
    </>
  )
}
